import { useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';

import {
  findExitMilestoneByRoute,
  getCompletedMilestoneIds,
  getFirstIncompleteMilestone,
  getResumePath,
  getWizardVariant,
  hasExited,
} from '@/lib/wizard/wizard';
import type { RoutePath, WizardProgress, WizardVariantId } from '@/lib/wizard/wizard';
import { FALLBACK_WIZARD_VARIANT_ID } from '@/lib/wizard/wizard.config';

import type { SessionStoreState } from './use-session-store/session-store-schema';
import { useSessionStore } from './use-session-store/use-session-store';

export function useWizardNavigation(variantIdOverride?: WizardVariantId): WizardProgress {
  const sessionState = useSessionStore();
  const pathname = useLocation({ select: (loc) => loc.pathname }) as RoutePath;

  const variantId = variantIdOverride ?? sessionState.wizardVariantId ?? FALLBACK_WIZARD_VARIANT_ID;
  const variant = useMemo(() => getWizardVariant(variantId), [variantId]);

  return useMemo(() => {
    const completedMilestoneIds = getCompletedMilestoneIds(sessionState, variant.milestones);

    // Check for exit state (from session or current route)
    const exit = hasExited(sessionState)
      ? variant.exitMilestones.find((t) => t.id === sessionState.exitMilestone)
      : findExitMilestoneByRoute(variant, pathname);

    if (exit) {
      return {
        variant,
        currentMilestone: null,
        currentMilestoneIndex: -1,
        exitMilestone: exit,
        resumePath: exit.route ?? variant.startPath,
        completedMilestoneIds,
      };
    }

    const firstIncomplete = getFirstIncompleteMilestone(sessionState, variant.milestones);

    if (!firstIncomplete) {
      // All milestones complete but not exited - default to last milestone
      const lastMilestone = variant.milestones[variant.milestones.length - 1];
      return {
        variant,
        currentMilestone: lastMilestone ?? null,
        currentMilestoneIndex: variant.milestones.length - 1,
        exitMilestone: null,
        resumePath: lastMilestone?.startPath ?? variant.startPath,
        completedMilestoneIds,
      };
    }

    const currentMilestoneIndex = variant.milestones.indexOf(firstIncomplete);

    return {
      variant,
      currentMilestone: firstIncomplete,
      currentMilestoneIndex,
      exitMilestone: null,
      resumePath: firstIncomplete.startPath,
      completedMilestoneIds,
    };
  }, [sessionState, variant, pathname]);
}

export function getSessionResumePath(
  sessionState: SessionStoreState,
  variantId?: WizardVariantId
): RoutePath {
  const variant = getWizardVariant(
    variantId ?? sessionState.wizardVariantId ?? FALLBACK_WIZARD_VARIANT_ID
  );
  return getResumePath(sessionState, variant);
}
